import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../../components/Navbar";
import { trackEvent } from "../../firebase/analytics";
import { industryData } from "./industryData";
import { useEffect } from "react";
import "../../styles/IndustryDetails.css";

import {
  MapPin,
  ShieldCheck,
  ClipboardCheck,
  Clock3,
  CalendarDays,
  BarChart3,
  FileText,
  Briefcase,
} from "lucide-react";

export default function IndustryDetails() {
  const { industryKey } = useParams();
  const navigate = useNavigate();

  const activeIndustry = industryData[industryKey];

  useEffect(() => {
    trackEvent("industry_detail_page_view", {
      industry: industryKey,
      page: `/industries/${industryKey}`,
    });
  }, [industryKey]);

  const challengeIcons = [MapPin, ShieldCheck, ClipboardCheck, Clock3];

  const featureIcons = [
    CalendarDays,
    MapPin,
    ClipboardCheck,
    FileText,
    BarChart3,
    Briefcase,
  ];

  const handleRegisterClick = (location) => {
    trackEvent("industry_detail_cta_click", {
      industry: industryKey,
      location,
      destination: "/register",
    });

    navigate("/register");
  };

  if (!activeIndustry) {
    return (
      <>
        <Navbar />
        <main className="industry-details-page">
          <section className="industry-details-not-found">
            <span className="industry-details-eyebrow">Industry</span>
            <h1>Industry not found</h1>
            <p>The industry page you are looking for does not exist.</p>

            <button
              className="industry-details-primary-btn"
              onClick={() => navigate("/industries")}
            >
              Back to Industries
            </button>
          </section>
        </main>
      </>
    );
  }

  return (
    <>
      <Navbar />

      <main className="industry-details-page">
        <section className="industry-details-hero">
          <div className="industry-details-hero-copy">
            <span className="industry-details-eyebrow">
              {activeIndustry.eyebrow}
            </span>

            <h1>{activeIndustry.title}</h1>

            <p>{activeIndustry.description}</p>

            <div className="industry-details-actions">
              <button
                className="industry-details-primary-btn"
                onClick={() => handleRegisterClick("industry_detail_hero")}
              >
                Get Started Free →
              </button>

              <button
                className="industry-details-secondary-btn"
                onClick={() => navigate("/pricing")}
              >
                View Pricing
              </button>
            </div>

            <div className="industry-details-proof-row">
              <div>
                <strong>Location Verified</strong>
                <span>Clock in from job sites</span>
              </div>

              <div>
                <strong>Task Accountability</strong>
                <span>Checklists & approvals</span>
              </div>

              <div>
                <strong>Operational Visibility</strong>
                <span>Real-time insights</span>
              </div>
            </div>
          </div>

          <div className="industry-details-hero-visual">
            <img
              src={activeIndustry.image}
              alt={`${activeIndustry.eyebrow} workforce management`}
              className="industry-details-hero-image"
              loading="lazy"
              decoding="async"
            />
            <div className="industry-details-stats-card">
              {(activeIndustry.stats || []).map((stat) => (
                <div key={stat.label}>
                  <strong>{stat.value}</strong>
                  <span>{stat.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="industry-details-section">
          <div className="industry-details-section-copy">
            <span className="industry-details-small-label">
              Common Challenges
            </span>

            <h2>
              Challenges {activeIndustry.eyebrow.toLowerCase()} teams face
            </h2>

            <p>
              From scheduling to attendance and daily responsibilities, managing
              teams gets complex without the right system.
            </p>
          </div>
          <div className="industry-details-card-grid challenge-grid">
            {activeIndustry.problems.map((problem, index) => {
              const ChallengeIcon =
                challengeIcons[index % challengeIcons.length];

              return (
                <div className="industry-details-card" key={problem}>
                  <div className="industry-details-card-icon">
                    <ChallengeIcon size={22} strokeWidth={2.2} />
                  </div>

                  <h3>{problem}</h3>

                  <p>
                    Oiiko helps reduce confusion and gives managers clearer
                    visibility into daily operations.
                  </p>
                </div>
              );
            })}
          </div>
        </section>

        <section className="industry-details-section industry-details-blue-section">
          <div className="industry-details-section-copy">
            <span className="industry-details-small-label">
              How Oiiko Helps
            </span>

            <h2>Features that fit the way your team works</h2>

            <p>
              These are the tools that matter most for{" "}
              {activeIndustry.eyebrow.toLowerCase()} teams.
            </p>

            <button
              className="industry-details-primary-btn"
              onClick={() => {
                trackEvent("industry_detail_features_click", {
                  industry: industryKey,
                  location: "industry_detail_features_section",
                  destination: "/features",
                });

                navigate("/features");
              }}
            >
              Explore All Features →
            </button>
          </div>

          <div className="industry-details-card-grid feature-grid">
            {activeIndustry.features.map((feature, index) => {
              const FeatureIcon = featureIcons[index % featureIcons.length];

              return (
                <div className="industry-details-card" key={feature.title}>
                  <div className="industry-details-card-icon">
                    <FeatureIcon size={22} strokeWidth={2.2} />
                  </div>

                  <h3>{feature.title}</h3>

                  <p>{feature.description}</p>
                </div>
              );
            })}
          </div>
        </section>

        <section className="industry-details-bottom-section">
          <div className="industry-details-bottom-image-wrap">
            <img
              src={activeIndustry.image}
              alt={`${activeIndustry.eyebrow} operations`}
              className="industry-details-bottom-image"
              loading="lazy"
              decoding="async"
            />
          </div>

          <div className="industry-details-bottom-copy">
            <span className="industry-details-small-label">
              Better Operations. Happier Teams.
            </span>

            <h2>Bring clarity to your daily workforce operations</h2>

            <p>
              Oiiko helps your team stay organized, accountable, and ready for
              every shift.
            </p>

            <ul>
              <li>Improve attendance visibility</li>
              <li>Reduce missed shifts and scattered communication</li>
              <li>Increase task accountability</li>
              <li>Prepare cleaner payroll reports</li>
            </ul>

            <button
              className="industry-details-primary-btn"
              onClick={() => handleRegisterClick("industry_detail_bottom")}
            >
              Get Started Free →
            </button>
          </div>
        </section>
      </main>
    </>
  );
}
